// Sub-fase 15.4.2 — Bundle inicial MÍNIMO (entry de build A):
//
// Este archivo es el entry point del `<script src="/react-app/dist/index.js">`
// que carga probly.html. Tras 15.4.2 NO importa nada de wagmi/wallet/lib —
// solo `import type` para las firmas (elidido en runtime, sin coste de
// bundle). El chunk wallet se carga vía dynamic import a una URL absoluta
// computada en runtime (`new URL(...).href`), lo que IMPIDE a Vite analizar
// el grafo y arrastrar las deps al bundle inicial.
//
// Arquitectura 2-builds (necesaria — single build con manualChunks fracasa):
//   - Build A (vite.config.ts):        src/main.ts → dist/index.js (este).
//   - Build B (vite.config.wallet.ts): src/Web3App.tsx → dist/wallet/index.js
//     con sus propios chunks vendor (react, privy, walletconnect, etc.).
//
// Por qué 2 builds: con un solo `vite build`, Vite analiza estáticamente el
// `import('./Web3App')` y emite hard imports a los chunks deps de Web3App
// en index.js (incluso con modulePreload:false), arrastrando ~1,5 MB al
// bundle inicial. La única forma de evitarlo es ocultar el path con
// `/* @vite-ignore */` y usar URL string que Vite no resuelve estáticamente
// — pero entonces Vite no emite el chunk Web3App. Solución: 2 builds
// separados con grafos independientes.
//
// Antes de 15.4.2: todo en un único bundle 5,4 MB / 1,58 MB gzipped, primer
// paint 5-8s en 4G. Tras 15.4.2: bundle inicial <1 KB gzipped, primer paint
// instantáneo. El monolito vanilla NO nota el cambio — todas las funciones
// de window.__probly_web3 ya retornaban Promise antes; los wrappers async
// aquí solo añaden un await del chunk wallet la primera vez.

import type { Web3Module } from './Web3App';

// URL del entry del build B (chunk wallet). Computada en runtime relativa al
// index.js que la carga, así funciona desde cualquier sub-path donde se
// sirva el bundle (probly.info la sirve desde /react-app/dist/, otros entornos
// pueden montar en otro path sin recompilar).
//
// `import(/* @vite-ignore */ WALLET_URL)` es CRÍTICO: el comentario impide
// que Vite analice estáticamente el path y arrastre Web3App al grafo del
// bundle inicial.
//
// Path como VARIABLE (no literal en el `new URL`) es también crítico:
// `new URL('./wallet/index.js', import.meta.url)` con literal hace que Vite
// lo interprete como asset estático y resuelva al filesystem source — en
// nuestro caso `src/wallet/index.ts` (que existe) acabaría inline como data
// URL base64. Con variable, Vite no toca el `new URL` en build.
const WALLET_PATH = 'wallet/index.js';
const WALLET_URL = new URL(WALLET_PATH, import.meta.url).href;

let cached: Promise<Web3Module> | null = null;

function loadWeb3(): Promise<Web3Module> {
  if (cached) return cached;
  cached = (async (): Promise<Web3Module> => {
    const mountNode = document.getElementById('privy-mount');
    if (!mountNode) {
      throw new Error(
        '[probly-web3] <div id="privy-mount"> no encontrado en el DOM. ' +
        'Verifica que probly.html incluye el div antes de la tag <script ' +
        'src="/react-app/dist/index.js">.',
      );
    }
    const mod = (await import(/* @vite-ignore */ WALLET_URL)) as {
      mountWeb3App: (el: HTMLElement) => Promise<Web3Module>;
    };
    return mod.mountWeb3App(mountNode);
  })();
  // Si la primera carga falla (red caída descargando el chunk, error de
  // sintaxis del bundle, etc.), permitimos retry posterior en lugar de
  // quedar pegado al rejection cacheado.
  cached.catch(() => {
    cached = null;
  });
  return cached;
}

window.__probly_web3 = {
  readMarketState: async (...args) => (await loadWeb3()).readMarketState(...args),
  connectWallet: async (...args) => (await loadWeb3()).connectWallet(...args),
  connectPrivy: async (...args) => (await loadWeb3()).connectPrivy(...args),
  connectWalletConnect: async (...args) => (await loadWeb3()).connectWalletConnect(...args),
  getConnectedAddress: async () => (await loadWeb3()).getConnectedAddress(),
  disconnectWallet: async (...args) => (await loadWeb3()).disconnectWallet(...args),
  getApprovalStatus: async (...args) => (await loadWeb3()).getApprovalStatus(...args),
  getOnChainHoldings: async (...args) => (await loadWeb3()).getOnChainHoldings(...args),
  getWalletBalances: async (...args) => (await loadWeb3()).getWalletBalances(...args),
  // PRE18.30: sell-all real iterativo (returnAmount con calcSellAmount + margen 5 bps).
  calcSellAllUsdcReturn: async (...args) => (await loadWeb3()).calcSellAllUsdcReturn(...args),
  // PRE18.31: preview USDC tras redeem + firma CTF.redeemPositions.
  previewRedeem: async (...args) => (await loadWeb3()).previewRedeem(...args),
  approveUsdcMax: async (...args) => (await loadWeb3()).approveUsdcMax(...args),
  setCtfApprovalForAll: async (...args) => (await loadWeb3()).setCtfApprovalForAll(...args),
  executeBuy: async (...args) => (await loadWeb3()).executeBuy(...args),
  executeSell: async (...args) => (await loadWeb3()).executeSell(...args),
  redeemPositions: async (...args) => (await loadWeb3()).redeemPositions(...args),
  // PRE18.23 B2: reusar firma SIWE del signup para JWT inicial sin 2ª firma.
  primeWalletConnectSiweJwt: async (...args) =>
    (await loadWeb3()).primeWalletConnectSiweJwt(...args),
  // PRE18.23 B3: invalidación externa del JWT cache WC (debug / tests).
  clearWalletConnectSiweJwt: async (...args) =>
    (await loadWeb3()).clearWalletConnectSiweJwt(...args),
  // Sub-fase 16.2: generación de QR client-side para pantalla "Mi wallet".
  generateQRCanvas: async (...args) => (await loadWeb3()).generateQRCanvas(...args),
  // Sub-fase 16.3 paso 3: EIP-55 strict + Check 1 eth_getCode para retirada.
  // El paso 2 vanilla cubre formato relaxed (lowercase o uppercase puro); el
  // checksum EIP-55 estricto (mixed-case) y el chequeo de bytecode se hacen
  // aquí vía wagmi/viem en el chunk wallet. Asumimos chunk ya caliente
  // porque getWalletBalances se invoca al renderizar la pantalla.
  validateAddressEip55: async (...args) => (await loadWeb3()).validateAddressEip55(...args),
  checkAddressIsEoa: async (...args) => (await loadWeb3()).checkAddressIsEoa(...args),
  // Sub-fase 16.3 paso 4: preview gas + balance POL.
  estimateTransferGas: async (...args) => (await loadWeb3()).estimateTransferGas(...args),
  // Sub-fase 16.3 paso 5: firma transfer USDC + 2 confirmaciones.
  executeUsdcTransfer: async (...args) => (await loadWeb3()).executeUsdcTransfer(...args),
  // Onramp fase 1 (ago 2026): compra de USDC integrada (funding de Privy,
  // solo cuentas de correo). El botón "Añadir fondos" de Mi wallet la invoca.
  fundEmbeddedWallet: async () => (await loadWeb3()).fundEmbeddedWallet(),
  // Sub-fase 16.2: flag síncrona — leemos la env var también en el build A
  // para que vanilla pueda consultarla sin disparar la carga del chunk
  // wallet (~750 KB). Fuente única: misma var VITE_MAINNET_READY que en
  // Web3App.tsx; los dos builds Vite la inlinean idénticamente.
  mainnetReady: import.meta.env.VITE_MAINNET_READY === 'true',
};

console.log(
  '[probly-web3] Lazy loader montado. window.__probly_web3 disponible. ' +
  'Chunk wallet se descargará desde ' + WALLET_URL + ' la primera vez ' +
  'que se invoque cualquier helper.',
);
